import { getAccessToken } from '@/lib/api'

/** Shapes the reporting screens read. Mirrors the server's report records. */

export interface SalesDay {
  date: string
  saleCount: number
  grossUsd: number
  discountUsd: number
  netUsd: number
  vatUsd: number
  refundsUsd: number
  costUsd: number
  grossProfitUsd: number
}

export interface BreakdownRow {
  label: string
  saleCount: number
  netUsd: number
  costUsd: number
  grossProfitUsd: number
}

export interface TopProduct {
  productId: number
  name: string
  sku: string
  quantity: number
  unit: string
  netUsd: number
  costUsd: number
  grossProfitUsd: number
}

export interface SalesReport {
  from: string
  to: string
  /** The zone the days were cut on. Shown so a total can be explained. */
  timeZone: string
  saleCount: number
  refundCount: number
  voidCount: number
  grossUsd: number
  discountUsd: number
  netUsd: number
  vatUsd: number
  refundsUsd: number
  costUsd: number
  grossProfitUsd: number
  averageSaleUsd: number
  days: SalesDay[]
  byCategory: BreakdownRow[]
  byCashier: BreakdownRow[]
  byBranch: BreakdownRow[]
  byPaymentMethod: BreakdownRow[]
  topProducts: TopProduct[]
  /** The last day whose raw detail has been archived, or null if none has. */
  archivedThrough: string | null
  /**
   * Part of this period came from the daily summary rather than its own rows.
   *
   * Set so a screen can explain an empty breakdown as missing detail rather than as nothing having
   * sold. Every report carrying this has to show it, or a partial answer reads as a complete one.
   */
  detailArchived: boolean
}

export interface ProfitAndLoss {
  from: string
  to: string
  grossSalesUsd: number
  discountsUsd: number
  refundsUsd: number
  netRevenueUsd: number
  costOfGoodsUsd: number
  grossProfitUsd: number
  wasteUsd: number
  operatingExpensesUsd: number
  nonOperatingExpensesUsd: number
  netProfitUsd: number
  grossMarginPercent: number
  netMarginPercent: number
  /** Cash the owner took out. Below the profit line; never subtracted from it. */
  ownerDrawingsUsd: number
  ownerDrawingsLbp: number
  /** Profit for the period less what has already been taken out of it. */
  retainedUsd: number
  expenses: { category: string; isNonOperating: boolean; amountUsd: number }[]
  /** The last day whose raw detail has been archived, or null if none has. */
  archivedThrough: string | null
  /**
   * Part of this period came from the daily summary rather than its own rows.
   *
   * Set so a screen can explain an empty breakdown as missing detail rather than as nothing having
   * sold. Every report carrying this has to show it, or a partial answer reads as a complete one.
   */
  detailArchived: boolean
}

export interface VatReport {
  from: string
  to: string
  outputVatUsd: number
  refundVatUsd: number
  netOutputVatUsd: number
  inputVatUsd: number
  payableUsd: number
  bands: { rate: number; netUsd: number; vatUsd: number }[]
  /** The last day whose raw detail has been archived, or null if none has. */
  archivedThrough: string | null
  /**
   * Part of this period came from the daily summary rather than its own rows.
   *
   * Set so a screen can explain an empty breakdown as missing detail rather than as nothing having
   * sold. Every report carrying this has to show it, or a partial answer reads as a complete one.
   */
  detailArchived: boolean
}

export interface InventoryReport {
  totalCostValueUsd: number
  totalRetailValueUsd: number
  potentialMarginUsd: number
  productsInStock: number
  lowStockCount: number
  expiringCount: number
  byCategory: {
    category: string
    productCount: number
    quantity: number
    costValueUsd: number
    retailValueUsd: number
  }[]
  deadStock: {
    productId: number
    name: string
    sku: string
    quantity: number
    costValueUsd: number
    lastSoldAtUtc: string | null
    daysSinceSold: number | null
  }[]
}

export interface WasteVoidReport {
  from: string
  to: string
  wasteCount: number
  wasteCostUsd: number
  voidCount: number
  voidValueUsd: number
  wasteByReason: { label: string; count: number; costUsd: number; valueUsd: number }[]
  wasteByUser: { label: string; count: number; costUsd: number; valueUsd: number }[]
  voidsByUser: { label: string; count: number; costUsd: number; valueUsd: number }[]
}

export interface Dashboard {
  todaySalesUsd: number
  todaySaleCount: number
  todayGrossProfitUsd: number
  drawerUsd: number
  drawerLbp: number
  safeUsd: number
  safeLbp: number
  receivablesUsd: number
  lowStockCount: number
  expiringCount: number
  trend: SalesDay[]
  topProducts: TopProduct[]
}

/* ------------------------------------------------------------------ builder */

export type FieldKind = 'Dimension' | 'Measure'
export type ValueType = 'Text' | 'Money' | 'Quantity' | 'Number' | 'Date' | 'Percent'

export interface CatalogueField {
  id: string
  label: string
  kind: FieldKind
  valueType: ValueType
  aggregate: string
  group: string | null
}

export interface CatalogueDataset {
  id: string
  label: string
  explanation: string
  isDated: boolean
  fields: CatalogueField[]
}

export interface ReportFilterInput {
  fieldId: string
  operator: 'Equals' | 'NotEquals' | 'Contains' | 'GreaterThan' | 'LessThan'
  value: string
}

export interface ReportDefinition {
  dataset: string
  /** Field ids only. The server resolves them; nothing here is ever a column name. */
  columns: string[]
  from?: string | null
  to?: string | null
  branchId?: number | null
  filters?: ReportFilterInput[]
  sortFieldId?: string | null
  sortDescending?: boolean
  detailRows?: boolean
}

export interface ReportResult {
  dataset: string
  datasetLabel: string
  columns: { id: string; label: string; kind: FieldKind; valueType: ValueType; aggregate: string }[]
  rows: (string | number | null)[][]
  totals: (string | number | null)[]
  rowCount: number
  sourceRowCount: number
  timeZone: string
  /** The grouping made more rows than one report returns; these are the first of them. */
  truncated: boolean
}

/**
 * A report layout someone kept.
 *
 * The definition is a description of a question, never its answer — loading one re-runs it, so a
 * template opened in December reports December.
 */
export interface SavedReport {
  id: number
  name: string
  dataset: string
  datasetLabel: string
  definition: ReportDefinition
  updatedAtUtc: string
}

/** Most templates one person may keep. Mirrors the server, which is what actually enforces it. */
export const MAX_SAVED_REPORTS = 10

/**
 * Downloads a file from the API — a report CSV, or an archive ZIP from the platform panel.
 *
 * Fetched rather than linked, because a plain anchor cannot carry the bearer token — the access
 * token is held in memory only, never in a cookie or in storage, so the request has to be made
 * by script. The filename comes from the server's own Content-Disposition where it sends one.
 */
export async function downloadFile(
  url: string,
  fallbackName: string,
  body?: unknown,
): Promise<void> {
  const response = await fetch(url, {
    method: body === undefined ? 'GET' : 'POST',
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      Authorization: `Bearer ${getAccessToken() ?? ''}`,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!response.ok) {
    throw new Error('Could not download that file.')
  }

  const named = /filename="?([^";]+)"?/.exec(
    response.headers.get('content-disposition') ?? '',
  )?.[1]

  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = named ?? fallbackName
  anchor.click()
  URL.revokeObjectURL(objectUrl)
}

/** Formats one cell the way its column says it should read. */
export function formatCell(value: string | number | null, type: ValueType): string {
  if (value === null || value === undefined || value === '') {
    return '—'
  }

  if (type === 'Money') {
    return `$${Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  if (type === 'Date') {
    // A business date arrives as "2026-08-18" and is a whole day: it has no time to show, and
    // inventing midnight for it would read as a real moment. Anything carrying a time is a
    // moment — when a sale rang up, when a payment landed — and the time is half the answer to
    // "which one was this?", so it is shown.
    const text = String(value)
    const at = new Date(text)
    if (Number.isNaN(at.getTime())) return text

    return /^\d{4}-\d{2}-\d{2}$/.test(text)
      ? at.toLocaleDateString()
      : `${at.toLocaleDateString()} ${at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  }

  if (type === 'Percent') {
    return `${(Number(value) * 100).toFixed(0)}%`
  }

  if (type === 'Quantity' || type === 'Number') {
    return Number(value).toLocaleString(undefined, { maximumFractionDigits: 3 })
  }

  return String(value)
}
